import { Injectable, inject } from '@angular/core';
import Dexie, { Table } from 'dexie';
import { BehaviorSubject } from 'rxjs';
import { TenantService } from '../services/tenant.service';

export interface CartItem {
  id?: number;
  courseId: string;
  tenantId?: string;
}

class CartDB extends Dexie {
  cartItems!: Table<CartItem, number>;

  constructor() {
    super('Cart');
    this.version(1).stores({ cartItems: '++id, courseId' });
    this.version(2).stores({ cartItems: '++id, courseId, tenantId' });
  }
}

@Injectable({ providedIn: 'root' })
export default class CartService {
  private tenantService = inject(TenantService);
  private db = new CartDB();

  private _cartCount = new BehaviorSubject<number>(0);
  readonly cartCount$ = this._cartCount.asObservable();

  constructor() {
    this.refreshCount();
  }

  private getEffectiveTenantId(): string {
    return (
      this.tenantService.getTenantId() ||
      this.tenantService.getTenantSlug() ||
      'default'
    );
  }

  async refreshCount(): Promise<number> {
    try {
      const items = await this.getAllCartItems();
      const count = items.length;
      this._cartCount.next(count);
      return count;
    } catch {
      return 0;
    }
  }

  async addToCart(courseId: string): Promise<CartItem | null> {
    const tenantId = this.getEffectiveTenantId();
    const existing = await this.db.cartItems
      .where({ courseId, tenantId })
      .first();

    if (existing) {
      await this.refreshCount();
      return existing;
    }

    try {
      const id = await this.db.cartItems.add({ courseId, tenantId });
      const item = await this.db.cartItems.get(id);
      await this.refreshCount();
      return item ?? null;
    } catch (err) {
      console.error('Failed to add to cart:', err);
      return null;
    }
  }

  async getAllCartItems(): Promise<CartItem[]> {
    const tenantId = this.getEffectiveTenantId();
    try {
      return await this.db.cartItems.where({ tenantId }).toArray();
    } catch {
      // Fallback for unmigrated legacy items without tenantId
      const all = await this.db.cartItems.toArray();
      return all.filter((item) => !item.tenantId || item.tenantId === tenantId);
    }
  }

  async getCartItemByCourseId(courseId: string): Promise<CartItem | undefined> {
    const tenantId = this.getEffectiveTenantId();
    const item = await this.db.cartItems.where({ courseId, tenantId }).first();
    if (item) return item;
    return await this.db.cartItems.where({ courseId }).first();
  }

  async removeByCourseId(courseId: string): Promise<number> {
    const tenantId = this.getEffectiveTenantId();
    const count = await this.db.cartItems
      .where({ courseId, tenantId })
      .delete();
    await this.refreshCount();
    return count;
  }

  async clearAll(): Promise<void> {
    const tenantId = this.getEffectiveTenantId();
    await this.db.cartItems.where({ tenantId }).delete();
    await this.refreshCount();
  }
}
